import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import GoogleLogoMark from '../components/GoogleLogoMark';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { mapGoogleSignInError, needsEmailVerification, useAuth } from '../context/AuthContext';
import Seo from '../seo/Seo';
import { trackEvent } from '../utils/analytics';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [verifiedNotice, setVerifiedNotice] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, login, loginWithGoogle, requestPasswordReset, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectParam = (searchParams.get('redirect') || '').trim();
  const redirectTarget = redirectParam.startsWith('/') ? redirectParam : '';
  const from = location.state?.from?.pathname || redirectTarget || '/projects';
  const registerPath = redirectTarget
    ? `/register?redirect=${encodeURIComponent(redirectTarget)}`
    : '/register';

  useEffect(() => {
    if (searchParams.get('verified') === '1') {
      setVerifiedNotice(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!loading && isAuthenticated && !needsEmailVerification(user)) {
    return <Navigate to={from} replace />;
  }

  if (!loading && isAuthenticated && needsEmailVerification(user)) {
    return <Navigate to="/verify-email" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      setError('Please fill in email and password.');
      return;
    }

    try {
      setError('');
      const signedIn = await login({ email, password });
      if (needsEmailVerification(signedIn)) {
        navigate('/verify-email', { replace: true, state: { from } });
        return;
      }
      navigate(from, { replace: true });
    } catch (firebaseError) {
      if (
        firebaseError.code === 'auth/wrong-password' ||
        firebaseError.code === 'auth/invalid-credential' ||
        firebaseError.code === 'auth/user-not-found'
      ) {
        setError('Wrong email or password.');
      } else {
        setError('Unable to login. Please try again.');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setGoogleLoading(true);
      const { isNewUser } = await loginWithGoogle();
      if (isNewUser) {
        trackEvent('sign_up', { method: 'google' });
      }
      navigate(from, { replace: true });
    } catch (firebaseError) {
      setError(mapGoogleSignInError(firebaseError));
    } finally {
      setGoogleLoading(false);
    }
  };

  const mapResetPasswordError = (code) => {
    switch (code) {
      case 'auth/user-not-found':
        return 'No account was found with this email address.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many requests. Please wait and try again.';
      default:
        return 'Unable to send reset email. Please try again.';
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (!resetEmail.trim()) {
      setError('Please enter your email address.');
      setResetMessage('');
      return;
    }

    try {
      setError('');
      setResetMessage('');
      setResetLoading(true);
      await requestPasswordReset(resetEmail.trim());
      setResetMessage('Password reset email sent. Please check your inbox.');
      setShowResetForm(false);
      setResetEmail('');
    } catch (firebaseError) {
      setError(mapResetPasswordError(firebaseError?.code));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="page landing-bg auth-sheet">
      <Seo
        title="Login | MyRoomsAI"
        description="Sign in to MyRoomsAI to generate AI room redesigns, manage projects, and download your interior concepts."
        canonicalPath="/login"
        noindex
      />
      <Navbar />
      <main className="auth-page">
        <div className="auth-card card">
          <h1>Welcome back</h1>
          <p>Sign in to continue generating personalized room concepts.</p>

          {verifiedNotice ? (
            <p className="auth-verified-notice">Email verified successfully. You can now log in.</p>
          ) : null}

          <form onSubmit={handleSubmit} className="auth-form">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />

            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />

            <button type="submit" className="btn btn-primary full-width">
              Login
            </button>
            {error ? (
              <p className="auth-error" role="alert">
                {error}
              </p>
            ) : null}
            {resetMessage ? <p className="auth-inline-message">{resetMessage}</p> : null}
            <p className="auth-or">or</p>
            <button
              type="button"
              className="btn btn-ghost full-width btn-google-signin"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
            >
              {googleLoading ? null : <GoogleLogoMark size={20} />}
              {googleLoading ? 'Signing in…' : 'Continue with Google'}
            </button>
            <button
              type="button"
              className="btn btn-ghost full-width"
              onClick={() => navigate(registerPath)}
            >
              Register
            </button>
          </form>

          <button
            type="button"
            className="muted-link"
            onClick={() => {
              setError('');
              setResetMessage('');
              setResetEmail(email);
              setShowResetForm((prev) => !prev);
            }}
          >
            Forgot password?
          </button>
          {showResetForm ? (
            <form className="auth-form" onSubmit={handleForgotPassword}>
              <label htmlFor="reset-email">Reset password email</label>
              <input
                id="reset-email"
                type="email"
                placeholder="you@example.com"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary full-width" disabled={resetLoading}>
                {resetLoading ? 'Sending…' : 'Send reset email'}
              </button>
            </form>
          ) : null}
          <p className="switch-auth">
            No account yet? <Link to={registerPath}>Create one</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default LoginPage;
